/**
 * Pipeline Board View
 * ===================
 * Kanban-style board showing jobs grouped by pipeline stage.
 * Clear visual progression from New → Quote → Approved → Live → Completed → Financial → Closed
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Loader2,
  RefreshCw,
  ChevronRight,
  MapPin,
  User,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import axios from 'axios';
import {
  PIPELINE_COLUMNS,
  STATUS_LABELS,
  STATUS_COLORS,
  getJobsForColumn,
  getStatusLabel,
  getStatusColor,
} from '../lib/pipelineConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lhs_token') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============== COMPONENTS ==============

const JobCard = ({ job, onClick }) => {
  const statusColor = getStatusColor(job.status);
  const statusLabel = getStatusLabel(job.status);
  
  const isOverdue = job.sla_status === 'OVERDUE';
  const isDueSoon = job.sla_status === 'DUE_SOON';

  return (
    <div
      onClick={() => onClick(job)}
      className={`p-3 bg-white rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
        isOverdue ? 'border-red-300 border-l-4 border-l-red-500' : 
        isDueSoon ? 'border-amber-300 border-l-4 border-l-amber-500' : 
        'border-slate-200'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="font-semibold text-sm text-slate-800 truncate flex-1">
          {job.job_reference || job.id?.slice(0, 12)}
        </div>
        {isOverdue && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
      </div>
      
      {/* Title */}
      <p className="text-xs text-slate-600 line-clamp-2 mb-2">
        {job.title || job.description || 'No description'}
      </p>
      
      {/* Status Badge */}
      <Badge className={`${statusColor} border text-[10px] mb-2`}>
        {statusLabel}
      </Badge>
      
      {/* Meta */}
      <div className="space-y-1 text-[10px] text-slate-500">
        {job.site_name && (
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{job.site_name}</span>
          </div>
        )}
        {job.assigned_contractor_name && (
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span className="truncate">{job.assigned_contractor_name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const PipelineColumn = ({ column, jobs, onJobClick }) => {
  const count = jobs.length;
  
  return (
    <div className="flex flex-col min-w-[250px] max-w-[280px]">
      {/* Column Header */}
      <div className={`${column.bgColor} ${column.borderColor} border-b-2 rounded-t-lg px-3 py-2`}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm text-slate-700">{column.label}</h3>
          <Badge variant="secondary" className="text-xs">
            {count}
          </Badge>
        </div>
        <p className="text-[10px] text-slate-500 mt-0.5">
          {column.statuses.slice(0, 2).join(', ')}
          {column.statuses.length > 2 && '...'}
        </p>
      </div>
      
      {/* Jobs List */}
      <div className={`flex-1 ${column.bgColor} bg-opacity-30 p-2 space-y-2 rounded-b-lg min-h-[400px] max-h-[600px] overflow-y-auto`}>
        {jobs.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">No jobs</p>
        ) : (
          jobs.map((job) => (
            <JobCard key={job.id} job={job} onClick={onJobClick} />
          ))
        )}
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============

export default function PipelineBoard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/fm/jobs?limit=500');
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Load error:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleJobClick = (job) => {
    navigate(`/fm/jobs/${job.id}`);
  };

  // Group jobs by column
  const groupedJobs = {};
  PIPELINE_COLUMNS.forEach((column) => {
    groupedJobs[column.key] = getJobsForColumn(jobs, column);
  });

  // Calculate totals
  const totals = {
    total: jobs.length,
    open: jobs.filter(j => !['Paid', 'Closed', 'Cancelled'].includes(j.status)).length,
    overdue: jobs.filter(j => j.sla_status === 'OVERDUE').length,
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">{error}</p>
        <Button onClick={loadJobs} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-100 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Pipeline Board</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totals.total} jobs • {totals.open} open • {totals.overdue > 0 && (
              <span className="text-red-500">{totals.overdue} overdue</span>
            )}
          </p>
        </div>
        <Button onClick={loadJobs} disabled={loading} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Pipeline Legend */}
      <Card className="mb-6">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium text-slate-600">Flow:</span>
            {PIPELINE_COLUMNS.map((col, idx) => (
              <React.Fragment key={col.key}>
                <span className={`px-2 py-1 rounded ${col.bgColor} border ${col.borderColor}`}>
                  {col.label}
                </span>
                {idx < PIPELINE_COLUMNS.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Board */}
      {loading && jobs.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {PIPELINE_COLUMNS.map((column) => (
            <PipelineColumn
              key={column.key}
              column={column}
              jobs={groupedJobs[column.key] || []}
              onJobClick={handleJobClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
